import React, { useEffect, useMemo, useState } from "react";
import {
  collection as firestoreCollection,
  doc,
  getDoc,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "./firebase";
import { sendUserNotification } from "./notificationApi";
import {
  readMarketplaceCartItems,
  writeMarketplaceCartItems,
} from "./marketplaceCartStore";
import { EmptyState, PageHeader, StatCard, ToneBadge } from "./petaarUi";

const currency = (value) => `₹${Number(value || 0).toFixed(2)}`;

export default function MarketplaceCartPage({ user, onLoginRequired, onBackMarketplace }) {
  const [cartItems, setCartItems] = useState([]);
  const [shippingAddress, setShippingAddress] = useState("");
  const [placingOrder, setPlacingOrder] = useState(false);

  useEffect(() => {
    setCartItems(readMarketplaceCartItems());
  }, []);

  useEffect(() => {
    writeMarketplaceCartItems(cartItems);
  }, [cartItems]);

  useEffect(() => {
    const loadAddress = async () => {
      if (!user?.uid) {
        setShippingAddress("");
        return;
      }
      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        const data = snap.exists() ? snap.data() || {} : {};
        setShippingAddress(
          String(data.currentAddress || data.address || data.permanentAddress || ""),
        );
      } catch (_) {
        // Silent preload failure is fine here.
      }
    };
    loadAddress();
  }, [user?.uid]);

  const cartCount = useMemo(
    () => cartItems.reduce((sum, item) => sum + Math.max(1, Number(item.qty) || 1), 0),
    [cartItems],
  );

  const subtotal = useMemo(
    () =>
      cartItems.reduce(
        (sum, item) => sum + Math.max(1, Number(item.qty) || 1) * (Number(item.price) || 0),
        0,
      ),
    [cartItems],
  );

  const totalMrp = useMemo(() => subtotal * 1.18, [subtotal]);
  const discount = useMemo(() => Math.max(0, totalMrp - subtotal), [totalMrp, subtotal]);
  const deliveryFee = subtotal > 699 ? 0 : cartItems.length ? 49 : 0;
  const platformFee = cartItems.length ? 10 : 0;
  const finalTotal = Math.max(0, subtotal + deliveryFee + platformFee);

  const adjustQty = (productId, delta) => {
    setCartItems((prev) =>
      prev
        .map((item) =>
          item.productId === productId
            ? { ...item, qty: Math.max(1, Number(item.qty || 1) + delta) }
            : item,
        )
        .filter(Boolean),
    );
  };

  const removeItem = (productId) => {
    setCartItems((prev) => prev.filter((item) => item.productId !== productId));
  };

  const placeOrderForItem = async (item, buyerProfile) => {
    const quantity = Math.max(1, Number(item.qty) || 1);
    const productRef = doc(db, "marketplaceProducts", item.productId);
    const orderRef = doc(firestoreCollection(db, "marketplaceOrders"));
    const buyerEmail = String(user?.email || "");
    const buyerEmailLower = buyerEmail.toLowerCase();

    let providerEmail = "";
    let title = "";
    let category = "";
    let imageUrl = "";
    let unitPrice = 0;
    let providerId = "";

    await runTransaction(db, async (transaction) => {
      const productSnap = await transaction.get(productRef);
      if (!productSnap.exists()) throw new Error("Product not found");
      const productData = productSnap.data() || {};
      const isActive = productData.isActive === true;
      const stockQty = Math.max(0, Number(productData.stockQty) || 0);

      title = String(productData.title || item.title || "Product");
      category = String(productData.category || item.category || "");
      providerEmail = String(productData.providerEmail || item.providerEmail || "");
      providerId = String(productData.providerId || item.providerId || "");
      imageUrl = Array.isArray(productData.imageUrls)
        ? String(productData.imageUrls[0] || "")
        : String(item.imageUrl || "");
      unitPrice = Number(productData.price || item.price || 0);

      if (!isActive) throw new Error(`${title} is unavailable`);
      if (stockQty < quantity) throw new Error(`${title} has only ${stockQty} left`);
      if (unitPrice <= 0) throw new Error(`${title} has invalid price`);
      if (!providerId) throw new Error(`${title} missing provider info`);

      transaction.update(productRef, {
        stockQty: stockQty - quantity,
        updatedAt: serverTimestamp(),
      });

      transaction.set(orderRef, {
        productId: item.productId,
        productTitle: title,
        productCategory: category,
        productImageUrl: imageUrl,
        providerId,
        providerEmail,
        providerEmailLower: providerEmail.toLowerCase(),
        buyerUid: user.uid,
        buyerEmail,
        buyerEmailLower,
        buyerName: buyerProfile.name,
        buyerPhone: buyerProfile.phone,
        shippingAddress: buyerProfile.shippingAddress,
        note: "",
        quantity,
        unitPrice,
        totalAmount: unitPrice * quantity,
        status: "placed",
        paymentMode: "cod",
        paymentStatus: "pending",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    });

    if (providerEmail) {
      try {
        await sendUserNotification({
          recipientEmail: providerEmail,
          type: "marketplace_order",
          title: "New Product Order",
          message: `${buyerProfile.name} placed order for ${quantity} x ${title}.`,
          orderId: orderRef.id,
        });
      } catch (_) {
        // Notification failure is non-blocking.
      }
    }

    try {
      await sendUserNotification({
        recipientEmail: buyerEmail,
        type: "marketplace_order",
        title: "Order Placed",
        message: `Your order for ${quantity} x ${title} was sent to seller.`,
        orderId: orderRef.id,
      });
    } catch (_) {
      // Notification failure is non-blocking.
    }
  };

  const handleProceed = async () => {
    if (!cartItems.length) {
      alert("Your cart is empty.");
      return;
    }
    if (!user) {
      onLoginRequired?.();
      return;
    }
    const address = String(shippingAddress || "").trim();
    if (!address) {
      alert("Please add delivery address.");
      return;
    }

    const buyerProfile = {
      name: String(user.displayName || user.email || "Pet Owner"),
      phone: String(user.phoneNumber || ""),
      shippingAddress: address,
    };

    const failed = [];
    const success = [];
    setPlacingOrder(true);
    try {
      for (const item of cartItems) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await placeOrderForItem(item, buyerProfile);
          success.push(item.productId);
        } catch (error) {
          failed.push(`${item.title}: ${error.message || "Order failed"}`);
        }
      }
      if (success.length) {
        setCartItems((prev) => prev.filter((item) => !success.includes(item.productId)));
      }
      if (failed.length) {
        alert(`Some orders failed:\n${failed.join("\n")}`);
      } else {
        alert("Order placed successfully.");
      }
    } finally {
      setPlacingOrder(false);
    }
  };

  return (
    <div className="petaar-page">
      <PageHeader
        eyebrow="Marketplace Checkout"
        title="Review Cart And Place Order"
        subtitle="Confirm delivery details, review pricing, and place each item with its seller through Petaar."
        actions={
          <button className="petaar-button petaar-button--secondary" onClick={() => onBackMarketplace?.()}>
            Continue Shopping
          </button>
        }
      />

      <div className="petaar-stat-grid" style={{ marginBottom: 24 }}>
        <StatCard label="Items in cart" value={cartCount} tone="default" meta="Across all sellers" />
        <StatCard label="Subtotal" value={subtotal} prefix="₹" decimals={2} tone="accent" meta="Before fees" />
        <StatCard label="Discount" value={discount} prefix="₹" decimals={2} tone="success" meta="MRP savings" />
        <StatCard label="Payable total" value={finalTotal} prefix="₹" decimals={2} tone="warning" meta="Includes delivery and platform fee" />
      </div>

      <section className="petaar-card" style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          {[
            { label: "Cart", active: true },
            { label: "Address" },
            { label: "Review" },
            { label: "Payment" },
          ].map((step, index) => (
            <div key={step.label} style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 999,
                  display: "grid",
                  placeItems: "center",
                  fontWeight: 800,
                  border: step.active ? "1px solid rgba(26, 26, 219, 0.18)" : "1px solid var(--border)",
                  background: step.active ? "rgba(26, 26, 219, 0.08)" : "#fff",
                  color: step.active ? "var(--primary)" : "var(--muted)",
                }}
              >
                {index + 1}
              </div>
              <strong style={{ color: step.active ? "var(--primary)" : "var(--text)" }}>{step.label}</strong>
            </div>
          ))}
        </div>
      </section>

      <div className="petaar-split" style={{ alignItems: "start" }}>
        <div style={{ display: "grid", gap: 18 }}>
          <section className="petaar-card" style={{ display: "grid", gap: 14 }}>
            <div className="petaar-section-title">
              <div>
                <div className="petaar-kicker">Delivery</div>
                <h3 style={{ fontSize: "1.35rem" }}>Shipping address</h3>
              </div>
              {user ? <ToneBadge tone="success">Logged in</ToneBadge> : <ToneBadge tone="warning">Login required</ToneBadge>}
            </div>
            <textarea
              rows={3}
              value={shippingAddress}
              onChange={(event) => setShippingAddress(event.target.value)}
              placeholder="Enter complete delivery address"
            />
          </section>

          {cartItems.length === 0 ? (
            <EmptyState
              icon="🧺"
              title="Your cart is empty"
              description="Add products from the marketplace to review them here and place your order."
              actionLabel="Browse Products"
              onAction={() => onBackMarketplace?.()}
            />
          ) : (
            cartItems.map((item) => (
              <section key={item.productId} className="petaar-card" style={{ display: "grid", gap: 14 }}>
                <div style={{ display: "grid", gap: 16, gridTemplateColumns: "112px minmax(0, 1fr) auto" }}>
                  <div style={{ width: 112, aspectRatio: "1 / 1", borderRadius: 18, background: "var(--surface-alt)", border: "1px solid var(--border)", display: "grid", placeItems: "center", overflow: "hidden" }}>
                    {item.imageUrl ? (
                      <img src={item.imageUrl} alt={item.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    ) : (
                      <span style={{ color: "var(--muted)", fontSize: 12 }}>No image</span>
                    )}
                  </div>
                  <div>
                    <h3 style={{ fontSize: "1.4rem" }}>{item.title}</h3>
                    <div style={{ marginTop: 6, color: "var(--muted)" }}>{item.category || "General"}</div>
                    <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 10 }}>
                      <button className="petaar-button petaar-button--ghost" onClick={() => adjustQty(item.productId, -1)}>-</button>
                      <strong style={{ minWidth: 28, textAlign: "center" }}>{item.qty}</strong>
                      <button className="petaar-button petaar-button--ghost" onClick={() => adjustQty(item.productId, 1)}>+</button>
                    </div>
                  </div>
                  <div style={{ textAlign: "right", display: "grid", gap: 12, justifyItems: "end" }}>
                    <button className="petaar-button petaar-button--ghost" onClick={() => removeItem(item.productId)}>
                      Remove
                    </button>
                    <div style={{ fontSize: "1.65rem", fontWeight: 900 }}>{currency((Number(item.price) || 0) * (Number(item.qty) || 1))}</div>
                  </div>
                </div>
              </section>
            ))
          )}
        </div>

        <aside style={{ display: "grid", gap: 18, alignSelf: "start" }}>
          <section className="petaar-card" style={{ display: "grid", gap: 12 }}>
            <div className="petaar-kicker">Offers</div>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
              <strong>Unlock gifts at special prices</strong>
              <ToneBadge tone="accent">{Math.min(cartCount, 5)}/5 unlocked</ToneBadge>
            </div>
            <p style={{ margin: 0, color: "var(--muted)" }}>Add items worth ₹779 to unlock this milestone.</p>
          </section>

          <section className="petaar-card" style={{ display: "grid", gap: 12 }}>
            <div className="petaar-kicker">Coupon</div>
            <strong style={{ fontSize: "1.1rem" }}>BEST5</strong>
            <p style={{ margin: 0, color: "var(--muted)" }}>Save ₹5 using this coupon during checkout.</p>
            <button className="petaar-button petaar-button--secondary">Apply Coupon</button>
          </section>

          <section className="petaar-card" style={{ display: "grid", gap: 12 }}>
            <div className="petaar-section-title">
              <div>
                <div className="petaar-kicker">Price Details</div>
                <h3 style={{ fontSize: "1.2rem" }}>Summary ({cartCount} item)</h3>
              </div>
            </div>
            <div style={{ display: "grid", gap: 10, color: "var(--muted)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}><span>Total MRP</span><strong style={{ color: "var(--text)" }}>{currency(totalMrp)}</strong></div>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}><span>Discount on MRP</span><strong style={{ color: "var(--success)" }}>- {currency(discount)}</strong></div>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}><span>Delivery fee</span><strong style={{ color: "var(--text)" }}>{deliveryFee ? currency(deliveryFee) : "Free"}</strong></div>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}><span>Platform fee</span><strong style={{ color: "var(--text)" }}>{platformFee ? currency(platformFee) : "Free"}</strong></div>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, paddingTop: 10, borderTop: "1px solid var(--border)", fontWeight: 900, color: "var(--text)" }}>
                <span>Total payable</span>
                <span>{currency(finalTotal)}</span>
              </div>
            </div>
            <div className="petaar-notice success">You have saved {currency(discount)}</div>
            <button className="petaar-button" onClick={handleProceed} disabled={placingOrder}>
              {placingOrder ? "Processing..." : user ? "Proceed" : "Login To Proceed"}
            </button>
          </section>
        </aside>
      </div>
    </div>
  );
}
